import React, { useState } from 'react';
import { AlertTriangle, Fingerprint, Key, Lock, ShieldCheck, UserPlus, X } from 'lucide-react';

interface AuthLoginModalProps {
  isOpen: boolean;
  onClose?: () => void;
  onLogin: (email: string, password: string) => Promise<string | null>;
  onOpenRegisterModal?: () => void;
}

export const AuthLoginModal: React.FC<AuthLoginModalProps> = ({ isOpen, onClose, onLogin, onOpenRegisterModal }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (!isOpen) return null;
  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    setIsSubmitting(true);
    const result = await onLogin(email, password);
    if (result) setError(result);
    setIsSubmitting(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/90 backdrop-blur-md">
      <form onSubmit={submit} className="bg-slate-900 border border-slate-800 w-full max-w-md rounded-3xl p-6 space-y-5 shadow-2xl">
        <div className="flex items-center justify-between border-b border-slate-800 pb-4">
          <div className="flex items-center gap-3">
            <div className="p-2.5 rounded-2xl bg-emerald-500/10 border border-emerald-500/30 text-emerald-400"><Fingerprint className="w-6 h-6" /></div>
            <div><h2 className="text-lg font-black text-white">Sign in to GigaBalls FC</h2><p className="text-xs text-slate-400">Use your registered account credentials.</p></div>
          </div>
          {onClose && <button type="button" onClick={onClose} className="p-2 rounded-xl bg-slate-800 text-slate-400 hover:text-white"><X className="w-5 h-5" /></button>}
        </div>
        {error && <div className="p-3 rounded-2xl bg-red-500/10 border border-red-500/30 text-red-300 text-xs flex gap-2"><AlertTriangle className="w-4 h-4 shrink-0" />{error}</div>}
        <label className="block space-y-2"><span className="text-xs font-bold text-slate-300">Email</span><div className="flex items-center gap-2 bg-slate-950 border border-slate-800 rounded-2xl px-3"><Key className="w-4 h-4 text-slate-500" /><input required type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="w-full bg-transparent py-3 text-sm text-white outline-none" autoComplete="email" placeholder="owner@example.com" /></div></label>
        <label className="block space-y-2"><span className="text-xs font-bold text-slate-300">Password</span><div className="flex items-center gap-2 bg-slate-950 border border-slate-800 rounded-2xl px-3"><Lock className="w-4 h-4 text-slate-500" /><input required type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="w-full bg-transparent py-3 text-sm text-white outline-none" autoComplete="current-password" placeholder="Your password" /></div></label>
        <button disabled={isSubmitting} className="w-full py-3 bg-emerald-500 disabled:opacity-50 text-slate-950 font-black rounded-2xl transition">{isSubmitting ? 'Signing in…' : 'Sign in securely'}</button>
        <div className="flex items-center gap-2 text-[11px] text-slate-500"><ShieldCheck className="w-4 h-4 text-emerald-400" />Sessions are stored server-side; account switching is disabled.</div>
        {onOpenRegisterModal && <button type="button" onClick={onOpenRegisterModal} className="w-full py-2.5 bg-slate-800 text-emerald-400 font-bold rounded-2xl text-xs flex items-center justify-center gap-2"><UserPlus className="w-4 h-4" />Request Player Account</button>}
      </form>
    </div>
  );
};
